import { PropsWithChildren, useEffect, useRef, useState } from "react";
import Tooltip, { TooltipProps } from "./Tooltip";

interface OverflowTooltipProps extends TooltipProps {
    isOverlapping?: boolean;
    tooltipProps?: { placement?: TooltipProps["placement"]; align: TooltipProps["align"] };
}

/**
 * Tooltip shown only when its children are truncated
 * @param  {Node} children component wrapped by Tooltip, could be truncated
 * @param {string | Node} overlay text or component shown in a Tooltip
 * @param {CSSProperties} overlayStyle inner style of a Tooltip Tooltip
 * @param {boolean} isOverlapping shows whether Tooltip should overlap the content
 * @returns children component wrapped in a custom Tooltip
 */
const OverflowTooltip = ({
    children,
    overlay,
    overlayStyle,
    isOverlapping = true,
    tooltipProps,
    overlayClassName,
    // Truncated-cell tooltips are plain overflow labels: while the cursor sits on the
    // popover, the rc-tooltip overlay (rendered in a portal outside the scroll container)
    // swallows wheel events and the underlying table/page stops scrolling. Dismiss the
    // tooltip on wheel by default so the wheel reaches the scroll container again.
    hideOnWheel = true,
}: PropsWithChildren<OverflowTooltipProps>) => {
    const [isTooltipShown, setTooltipShown] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(([entry]) => {
            if (
                entry.target.scrollWidth > entry.target.clientWidth ||
                (entry.target.firstElementChild &&
                    entry.target.firstElementChild?.scrollHeight >
                        entry.target.firstElementChild?.clientHeight)
            ) {
                setTooltipShown(true);
            } else {
                setTooltipShown(false);
            }
        });

        if (ref.current) {
            resizeObserver.observe(ref.current);
        }
        const refCurrent = ref.current;
        return () => {
            if (refCurrent) {
                resizeObserver.unobserve(refCurrent);
            }
            setTooltipShown(false);
        };
    }, [overlay]);

    const overlappingProps = isOverlapping
        ? {
              overlayClassName: `light overlapText ${overlayClassName ?? ""}`,
              align: { offset: [-5, -25] },
              placement: "bottomLeft" as "bottomLeft",
          }
        : { overlayClassName };

    return (
        <Tooltip
            overlay={overlay}
            isHidden={!isTooltipShown}
            overlayStyle={overlayStyle}
            hideOnWheel={hideOnWheel}
            {...tooltipProps}
            {...overlappingProps}
        >
            <div
                ref={ref}
                style={{
                    whiteSpace: "pre",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                }}
            >
                {children}
            </div>
        </Tooltip>
    );
};

export default OverflowTooltip;
